/**
 * Piece size registry.
 *
 * `scale` multiplies each `<Part>`'s geometry — so "large" pieces are
 * physically bigger on the board. `cameraDistance` is a [0..1] parameter the
 * board's camera lerp uses to back the camera off when pieces grow, so the
 * whole piece stays in frame.
 */
export type PieceSizeId = 'small' | 'medium' | 'large';

export interface PieceSizeSpec {
  readonly id: PieceSizeId;
  readonly name: string;
  readonly scale: number;
  readonly cameraDistance: number;
}

export const PIECE_SIZES: readonly PieceSizeSpec[] = [
  { id: 'small', name: 'Small', scale: 0.75, cameraDistance: 0.35 },
  { id: 'medium', name: 'Medium', scale: 1.0, cameraDistance: 0.6 },
  { id: 'large', name: 'Large', scale: 1.35, cameraDistance: 0.85 },
];

// Registry invariants are guarded at module init so downstream callers can
// rely on a non-empty registry without non-null asserting at every site.
function requireAt<T>(arr: readonly T[], index: number, name: string): T {
  const entry = arr[index];
  if (!entry) throw new Error(`@strategy4kids/board-3d: ${name}[${index}] is out of range`);
  return entry;
}

export const DEFAULT_PIECE_SIZE: PieceSizeSpec = requireAt(PIECE_SIZES, 1, 'PIECE_SIZES');

export function pieceSizeById(id: PieceSizeId): PieceSizeSpec {
  return PIECE_SIZES.find((s) => s.id === id) ?? DEFAULT_PIECE_SIZE;
}
